// ===================================
// FEEDBACK FORM PAGE (PUBLIC)
// src/pages/FeedbackForm.js
// Stage 5: Public feedback submission page
// Users access this from email links with token
// ===================================

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import './FeedbackForm.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const FeedbackForm = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    rating: 0,
    wouldRecommend: null,
    whatWentWell: '',
    whatToImprove: '',
    comments: '',
    experienceAreas: {
      aiQuality: 0,
      audioQuality: 0,
      easeOfUse: 0,
      valueForMoney: 0
    }
  });

  // Check for quick rating from URL
  const quickRating = searchParams.get('rating');
  const isQuickSubmit = searchParams.get('quick') === 'true';

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await fetch(`${API_URL}/feedback/token/${token}`);
        const data = await response.json();

        if (!response.ok) {
          if (data.alreadySubmitted) {
            setError('already_submitted');
          } else {
            setError(data.message || 'Invalid or expired link');
          }
          setLoading(false);
          return;
        }

        setSessionInfo(data.session);

        // If quick rating, pre-fill and possibly auto-submit
        if (quickRating && isQuickSubmit) {
          const rating = parseInt(quickRating);
          if (rating >= 1 && rating <= 5) {
            setFormData(prev => ({ ...prev, rating }));
            // Auto-submit quick rating
            handleQuickSubmit(rating);
            return;
          }
        } else if (quickRating) {
          const rating = parseInt(quickRating);
          if (rating >= 1 && rating <= 5) {
            setFormData(prev => ({ ...prev, rating }));
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Token validation error:', err);
        setError('Unable to validate link. Please try again later.');
        setLoading(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setError('No feedback token provided');
      setLoading(false);
    }
  }, [token, quickRating, isQuickSubmit]);

  // Quick submit (one-click rating from email)
  const handleQuickSubmit = async (rating) => {
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/feedback/submit-quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, rating })
      });

      const data = await response.json();

      if (!response.ok && !data.alreadySubmitted) {
        throw new Error(data.message || 'Submission failed');
      }

      setFormData(prev => ({ ...prev, rating }));
      setSubmitted(true);
      setLoading(false);
    } catch (err) {
      console.error('Quick submit error:', err);
      setError(err.message);
      setLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Full form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.rating === 0) {
      alert('Please select a rating');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/feedback/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          rating: formData.rating,
          wouldRecommend: formData.wouldRecommend,
          whatWentWell: formData.whatWentWell || null,
          whatToImprove: formData.whatToImprove || null,
          comments: formData.comments || null,
          experienceAreas: Object.fromEntries(
            Object.entries(formData.experienceAreas).filter(([_, v]) => v > 0)
          )
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.alreadySubmitted) {
          setError('already_submitted');
          return;
        }
        throw new Error(data.message || 'Submission failed');
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle rating click
  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  // Handle recommend click
  const handleRecommendClick = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      wouldRecommend: prev.wouldRecommend === value ? null : value 
    }));
  };

  // Handle experience area rating
  const handleExperienceRating = (area, rating) => {
    setFormData(prev => ({
      ...prev,
      experienceAreas: {
        ...prev.experienceAreas,
        [area]: prev.experienceAreas[area] === rating ? 0 : rating
      }
    }));
  };

  // Render star rating
  const renderStarRating = (currentRating, onSelect, size = 'large') => {
    return (
      <div className={`star-rating star-rating-${size}`}>
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            className={`star-btn ${star <= currentRating ? 'active' : ''}`}
            onClick={() => onSelect(star)}
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
          >
            {star <= currentRating ? '★' : '☆'}
          </button>
        ))}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="feedback-form-page">
        <div className="feedback-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{isQuickSubmit ? 'Submitting your rating...' : 'Loading...'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="feedback-form-page">
        <div className="feedback-container">
          <div className="error-state">
            {error === 'already_submitted' ? (
              <>
                <div className="state-icon">✅</div>
                <h2>Feedback Already Received</h2>
                <p>Thank you! You've already submitted feedback for this session.</p>
                <p className="muted">We appreciate your input and use it to improve our platform.</p>
              </>
            ) : (
              <>
                <div className="state-icon">⚠️</div>
                <h2>Unable to Load Feedback Form</h2>
                <p>{error}</p>
                <p className="muted">The link may have expired or already been used.</p>
              </>
            )}
            <a href="/" className="home-link">Return to Home</a>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="feedback-form-page">
        <div className="feedback-container">
          <div className="success-state">
            <div className="state-icon success-icon">🎉</div>
            <h2>Thank You!</h2>
            <p>Your feedback has been submitted successfully.</p>
            
            <div className="submitted-rating">
              <span className="rating-label">You rated your experience:</span>
              <div className="rating-display">
                {'★'.repeat(formData.rating)}{'☆'.repeat(5 - formData.rating)}
              </div>
            </div>

            <p className="muted">
              Your feedback helps us improve the interview coaching experience for everyone.
            </p>

            <div className="success-actions">
              <a href="/" className="btn btn-primary">Return to Home</a>
              <a href="/app/dashboard" className="btn btn-secondary">Go to Dashboard</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="feedback-form-page">
      <div className="feedback-container">
        <div className="feedback-header">
          <div className="brand">
            <span className="brand-icon">🎯</span>
            <span className="brand-text">Career-Coach.AI</span>
          </div>
          <h1>Share Your Feedback</h1>
          <p>Help us improve your interview practice experience</p>
        </div>

        {/* Session Summary */}
        {sessionInfo && (
          <div className="session-summary">
            <h3>Your Session</h3>
            <div className="summary-items">
              <div className="summary-item">
                <span className="summary-label">Type</span>
                <span className="summary-value">
                  {sessionInfo.interviewType === 'voice' ? '🎙️ Voice' : 
                   sessionInfo.interviewType === 'phone' ? '📞 Phone' : '💬 Text'}
                </span>
              </div>
              {sessionInfo.categoryName && (
                <div className="summary-item">
                  <span className="summary-label">Category</span>
                  <span className="summary-value">{sessionInfo.categoryName}</span>
                </div>
              )}
              {sessionInfo.duration && (
                <div className="summary-item">
                  <span className="summary-label">Duration</span>
                  <span className="summary-value">{sessionInfo.duration} min</span>
                </div>
              )}
              {sessionInfo.overallScore && (
                <div className="summary-item">
                  <span className="summary-label">Score</span>
                  <span className={`summary-value score-value ${
                    sessionInfo.overallScore >= 80 ? 'score-high' :
                    sessionInfo.overallScore >= 60 ? 'score-medium' : 'score-low'
                  }`}>
                    {sessionInfo.overallScore}%
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="feedback-form">
          {/* Main Rating */}
          <div className="form-section rating-section">
            <label className="section-label">How was your overall experience? <span className="required">*</span></label>
            {renderStarRating(formData.rating, handleRatingClick, 'large')}
            <div className="rating-labels">
              <span>Poor</span>
              <span>Excellent</span>
            </div>
          </div>

          {/* Would Recommend */}
          <div className="form-section">
            <label className="section-label">Would you recommend Career-Coach.AI to others?</label>
            <div className="recommend-buttons">
              <button
                type="button"
                className={`recommend-btn ${formData.wouldRecommend === true ? 'active yes' : ''}`}
                onClick={() => handleRecommendClick(true)}
              >
                👍 Yes
              </button>
              <button
                type="button"
                className={`recommend-btn ${formData.wouldRecommend === false ? 'active no' : ''}`}
                onClick={() => handleRecommendClick(false)}
              >
                👎 No
              </button>
            </div>
          </div>

          {/* Experience Areas */}
          <div className="form-section">
            <label className="section-label">Rate specific areas (optional)</label>
            <div className="experience-areas">
              {[
                { key: 'aiQuality', label: 'AI Interview Quality', icon: '🤖' },
                { key: 'audioQuality', label: 'Audio/Video Quality', icon: '🔊' },
                { key: 'easeOfUse', label: 'Ease of Use', icon: '✨' },
                { key: 'valueForMoney', label: 'Value for Money', icon: '💰' }
              ].map(area => (
                <div key={area.key} className="experience-item">
                  <span className="experience-label">
                    <span className="experience-icon">{area.icon}</span>
                    {area.label}
                  </span>
                  {renderStarRating(
                    formData.experienceAreas[area.key],
                    (rating) => handleExperienceRating(area.key, rating),
                    'small'
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Text Feedback */}
          <div className="form-section">
            <label className="section-label">What went well?</label>
            <textarea
              value={formData.whatWentWell}
              onChange={(e) => setFormData(prev => ({ ...prev, whatWentWell: e.target.value }))}
              placeholder="Tell us what you liked about the experience..."
              rows={3}
            />
          </div>

          <div className="form-section">
            <label className="section-label">What could be improved?</label>
            <textarea
              value={formData.whatToImprove}
              onChange={(e) => setFormData(prev => ({ ...prev, whatToImprove: e.target.value }))}
              placeholder="Share any suggestions for improvement..."
              rows={3}
            />
          </div>

          <div className="form-section">
            <label className="section-label">Additional comments</label>
            <textarea
              value={formData.comments}
              onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
              placeholder="Any other thoughts you'd like to share..."
              rows={3}
            />
          </div>

          {/* Submit */}
          <div className="form-actions">
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || formData.rating === 0}
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
            <p className="privacy-note">
              Your feedback is anonymous and helps us improve.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;